'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import Image from 'next/image';
import Modal from 'react-modal';
import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLockKeyhole } from '@awesome.me/kit-94e593bf23/icons/classic/solid';
import { faHiveIconSmallLight } from '@awesome.me/kit-94e593bf23/icons/kit/custom';
import { faXmark } from '@awesome.me/kit-94e593bf23/icons/classic/light';
import { faMagnifyingGlassPlus } from '@awesome.me/kit-94e593bf23/icons/classic/regular';
import { decodeEntities } from '@/app/_lib/services/ClientUtil';
import Price from '@/app/_components/Price';
import ProductFavoriteMarker from '@/app/_components/ProductFavoriteMarker';
import PromoCode from '@/app/_components/PromoCode';
import { useConfirmDialog } from '@/app/_lib/_context';
import { useGlobalMessage, useCart } from '@/app/_lib/hooks';
import { trackRemoveFromCart, gaItemFromCartItem } from '@/app/_lib/analytics/ga4';

// Format unix timestamp to readable date
const formatBackorderDate = timestamp => {
  if (!timestamp) return null;
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  });
};
const CartOptions = t0 => {
  const $ = _c(2);
  const {
    options
  } = t0;
  if (!options) {
    return null;
  }
  const now = Date.now() / 1000;
  let t1;
  if ($[0] !== options) {
    t1 = <>{Object.entries(options).map((option, index) => {
        const opt = option[1];
        const hasUpcharge = opt.option_price > 0 || opt.original_option_price > 0;
        const isDiscounted = opt.original_option_price > 0 && opt.original_option_price > opt.option_price;
        let optionBackorderMessage = null;
        if (opt.production_hold_expiration_date && opt.production_hold_expiration_date > now) {
          optionBackorderMessage = {
            prompt: "production hold",
            message: `estimated availability ${formatBackorderDate(opt.production_hold_expiration_date)}`
          };
        } else {
          if (opt.backorder_expiration_date && opt.backorder_expiration_date > now) {
            optionBackorderMessage = {
              prompt: "backordered",
              message: `estimated in stock ${formatBackorderDate(opt.backorder_expiration_date)}`
            };
          }
        }
        return <div key={index} className="capitalize mb-2 text-sm"><span className="text-ink-light mr-1">{opt.selection_label}:</span><span className="font-medium">{opt.option_name}</span>{hasUpcharge && <span className="ml-1">{isDiscounted && <span className="line-through text-ink-light mr-1">+ <Price amount={opt.original_option_price} /></span>}<span className="font-medium text-black">+ <Price amount={opt.option_price} /></span></span>}{opt.option_photo && <div className="mt-1"><Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/option_photos/${opt.option_photo}`} alt={opt.option_name} width={35} height={20} /></div>}{optionBackorderMessage && <div className="text-xs mt-1"><span className="text-red">{optionBackorderMessage.prompt}</span> {optionBackorderMessage.message}</div>}</div>;
      })}</>;
    $[0] = options;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const SwatchImageModal = t0 => {
  const $ = _c(8);
  const {
    swatch,
    isOpen,
    onClose
  } = t0;
  const t1 = swatch ? `${swatch.swatch_name} swatch image` : "Swatch image";
  let t2;
  if ($[0] !== onClose || $[1] !== swatch) {
    t2 = swatch && <><button onClick={onClose} className="absolute -top-3 -right-3 z-10 w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-lg text-dark hover:text-primary transition-colors" aria-label="Close"><FontAwesomeIcon icon={faXmark} className="text-lg" /></button><div className="p-2">{swatch.swatch_photo_large_url ? <Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${swatch.swatch_photo_large_url}`} alt={swatch.swatch_name} width={500} height={500} className="w-full h-auto" /> : <div className="flex flex-col items-center justify-center aspect-square w-full max-w-[500px]"><Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/swatch_photos/${swatch.swatch_photo}`} alt={swatch.swatch_name} width={200} height={200} /><p className="text-xs text-light mt-2">Large image not available</p></div>}</div><div className="p-3 flex items-end justify-between"><div>{swatch.swatch_set_label && <div className="text-xs text-light mb-0.5">{swatch.swatch_set_label}</div>}<div className="text-xs font-medium">{swatch.swatch_name}{swatch.swatch_code && <span className="pl-0.5 text-light font-normal">({swatch.swatch_code})</span>}</div></div><FontAwesomeIcon icon={faHiveIconSmallLight} className="text-primary text-3xl mb-0.5" /></div></>;
    $[0] = onClose;
    $[1] = swatch;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isOpen || $[4] !== onClose || $[5] !== t1 || $[6] !== t2) {
    t3 = <Modal isOpen={isOpen} onRequestClose={onClose} bodyOpenClassName="ReactModal__Body--open" htmlOpenClassName="ReactModal__Html--open" contentLabel={t1} overlayClassName="fixed inset-0 z-[1055] flex items-center justify-center bg-modal-overlay" className="relative bg-white rounded-lg w-[calc(100%-60px)] max-w-[500px] max-h-[90vh] outline-none">{t2}</Modal>;
    $[3] = isOpen;
    $[4] = onClose;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const CartSwatches = t0 => {
  const $ = _c(9);
  const {
    swatches
  } = t0;
  const [viewingSwatch, setViewingSwatch] = useState(null);
  if (!swatches) {
    return null;
  }
  const now = Date.now() / 1000;
  let t1;
  if ($[0] !== swatches) {
    t1 = Object.entries(swatches).map((swatch, index) => {
      const sw = swatch[1];
      const swatchPrice = sw.swatch_price || sw.price || 0;
      const originalPrice = sw.original_swatch_price || 0;
      const hasUpcharge = swatchPrice > 0 || originalPrice > 0;
      const isDiscounted = originalPrice > 0 && originalPrice > swatchPrice;
      let swatchBackorderMessage = null;
      if (sw.production_hold_expiration_date && sw.production_hold_expiration_date > now) {
        swatchBackorderMessage = {
          prompt: "production hold",
          message: `estimated availability ${formatBackorderDate(sw.production_hold_expiration_date)}`
        };
      } else {
        if (sw.backorder_expiration_date && sw.backorder_expiration_date > now) {
          swatchBackorderMessage = {
            prompt: "backordered",
            message: `estimated in stock ${formatBackorderDate(sw.backorder_expiration_date)}`
          };
        }
      }
      return <div key={index} className="capitalize mb-2 text-sm"><span className="text-ink-light mr-1">{sw.swatch_set_label}:</span><span className="font-medium">{sw.swatch_name}{sw.swatch_code && <span className="font-normal text-ink-light ml-0.5">({sw.swatch_code})</span>}</span>{hasUpcharge && <span className="ml-1">{isDiscounted && <span className="line-through text-ink-light mr-1">+ <Price amount={originalPrice} /></span>}<span className="font-medium text-black">+ <Price amount={swatchPrice} /></span></span>}{sw.swatch_photo && <button onClick={() => setViewingSwatch(sw)} className="mt-1 flex items-center gap-1.5 font-extralight text-ink-light hover:text-primary transition-colors" title="View larger"><Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/swatch_photos/${sw.swatch_photo}`} alt={sw.swatch_name} width={30} height={30} className="shadow-sm" /><FontAwesomeIcon icon={faMagnifyingGlassPlus} className="text-3xs" /><span className="text-xs">View</span></button>}{swatchBackorderMessage && <div className="text-xs mt-1"><span className="text-red">{swatchBackorderMessage.prompt}</span> {swatchBackorderMessage.message}</div>}</div>;
    });
    $[0] = swatches;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !!viewingSwatch;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setViewingSwatch(null);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2 || $[4] !== viewingSwatch) {
    t4 = <SwatchImageModal swatch={viewingSwatch} isOpen={t2} onClose={t3} />;
    $[3] = t2;
    $[4] = viewingSwatch;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t4) {
    t5 = <>{t1}{t4}</>;
    $[6] = t1;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
const InlineQtyEditor = t0 => {
  const $ = _c(47);
  const {
    cartItem,
    itemIndex,
    allowEdit: t1
  } = t0;
  const allowEdit = t1 === undefined ? false : t1;
  const [isEditing, setIsEditing] = useState(false);
  const [qty, setQty] = useState(cartItem.quantity);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const {
    confirm
  } = useConfirmDialog();
  const addGlobalMessage = useGlobalMessage();
  const {
    updateItem,
    deleteItem,
    saveForLater
  } = useCart();
  let t2;
  if ($[0] !== cartItem || $[1] !== itemIndex || $[2] !== qty || $[3] !== updateItem) {
    t2 = async () => {
      if (qty === cartItem.quantity) {
        setIsEditing(false);
        return;
      }
      setSaving(true);
      const updatedCartItem = {
        ...cartItem,
        quantity: qty
      };
      await updateItem(itemIndex, updatedCartItem);
      setSaving(false);
      setIsEditing(false);
    };
    $[0] = cartItem;
    $[1] = itemIndex;
    $[2] = qty;
    $[3] = updateItem;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleSave = t2;
  let t3;
  if ($[5] !== addGlobalMessage || $[6] !== cartItem || $[7] !== confirm || $[8] !== deleteItem || $[9] !== itemIndex || $[10] !== saveForLater) {
    t3 = async () => {
      const result = await confirm({
        title: "Remove from Cart?",
        message: `What would you like to do with "${decodeEntities(cartItem.product.sub_product_name)}"?`,
        buttons: [{
          label: "Remove from Cart",
          action: "remove",
          variant: "primary"
        }, {
          label: "Save for Later",
          action: "save",
          variant: "secondary"
        }]
      });
      if (result === "remove") {
        setDeleting(true);
        trackRemoveFromCart({
          value: cartItem.total_price,
          items: [gaItemFromCartItem(cartItem, itemIndex)]
        });
        await deleteItem(itemIndex);
        addGlobalMessage({
          message: "Item removed from cart",
          messageType: "success"
        });
        setDeleting(false);
      } else {
        if (result === "save") {
          setDeleting(true);
          await saveForLater(itemIndex);
          addGlobalMessage({
            message: `${decodeEntities(cartItem.product.sub_product_name)} was saved for later`,
            messageType: "success"
          });
          setDeleting(false);
        }
      }
    };
    $[5] = addGlobalMessage;
    $[6] = cartItem;
    $[7] = confirm;
    $[8] = deleteItem;
    $[9] = itemIndex;
    $[10] = saveForLater;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleDelete = t3;
  let t4;
  if ($[12] !== cartItem.quantity || $[13] !== handleSave) {
    t4 = e => {
      if (e.key === "Enter") {
        handleSave();
      } else {
        if (e.key === "Escape") {
          setQty(cartItem.quantity);
          setIsEditing(false);
        }
      }
    };
    $[12] = cartItem.quantity;
    $[13] = handleSave;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handleKeyDown = t4;
  if (isEditing) {
    let t5;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <span className="text-ink-light">Qty:</span>;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = e_0 => setQty(Math.max(1, parseInt(e_0.target.value) || 1));
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t7;
    if ($[17] !== handleKeyDown || $[18] !== qty || $[19] !== saving) {
      t7 = <input type="number" min="1" max="99" value={qty} onChange={t6} onKeyDown={handleKeyDown} className="w-12 px-1 py-0.5 text-xs border border-gray-300 rounded text-center focus:outline-none focus:border-primary" autoFocus={true} disabled={saving} />;
      $[17] = handleKeyDown;
      $[18] = qty;
      $[19] = saving;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    const t8 = saving || deleting;
    const t9 = saving ? "..." : "Save";
    let t10;
    if ($[21] !== handleSave || $[22] !== t8 || $[23] !== t9) {
      t10 = <button onClick={handleSave} disabled={t8} className="text-3xs bg-primary text-white px-2 py-0.5 rounded-sm hover:bg-primary-hover disabled:opacity-50">{t9}</button>;
      $[21] = handleSave;
      $[22] = t8;
      $[23] = t9;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    let t11;
    if ($[25] !== cartItem.quantity) {
      t11 = () => {
        setIsEditing(false);
        setQty(cartItem.quantity);
      };
      $[25] = cartItem.quantity;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    const t12 = saving || deleting;
    let t13;
    if ($[27] !== t11 || $[28] !== t12) {
      t13 = <button onClick={t11} disabled={t12} className="text-3xs text-ink-light underline hover:text-dark disabled:opacity-50">Cancel</button>;
      $[27] = t11;
      $[28] = t12;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    const t14 = saving || deleting;
    const t15 = deleting ? "..." : "Remove From Cart";
    let t16;
    if ($[30] !== handleDelete || $[31] !== t14 || $[32] !== t15) {
      t16 = <button onClick={handleDelete} disabled={t14} className="text-3xs text-ink-light underline hover:text-red ml-4 disabled:opacity-50">{t15}</button>;
      $[30] = handleDelete;
      $[31] = t14;
      $[32] = t15;
      $[33] = t16;
    } else {
      t16 = $[33];
    }
    let t17;
    if ($[34] !== t10 || $[35] !== t13 || $[36] !== t16 || $[37] !== t7) {
      t17 = <div className="text-sm mb-2 flex items-center gap-1.5">{t5}{t7}{t10}{t13}{t16}</div>;
      $[34] = t10;
      $[35] = t13;
      $[36] = t16;
      $[37] = t7;
      $[38] = t17;
    } else {
      t17 = $[38];
    }
    return t17;
  }
  let t5;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="text-ink-light mr-1">Qty:</span>;
    $[39] = t5;
  } else {
    t5 = $[39];
  }
  let t6;
  if ($[40] !== cartItem.quantity) {
    t6 = <span className="font-medium">{cartItem.quantity}</span>;
    $[40] = cartItem.quantity;
    $[41] = t6;
  } else {
    t6 = $[41];
  }
  let t7;
  if ($[42] !== allowEdit) {
    t7 = allowEdit && <button onClick={() => setIsEditing(true)} className="text-3xs underline text-ink-light hover:text-primary ml-1.5">Edit</button>;
    $[42] = allowEdit;
    $[43] = t7;
  } else {
    t7 = $[43];
  }
  let t8;
  if ($[44] !== t6 || $[45] !== t7) {
    t8 = <div className="text-sm mb-2">{t5}{t6}{t7}</div>;
    $[44] = t6;
    $[45] = t7;
    $[46] = t8;
  } else {
    t8 = $[46];
  }
  return t8;
};
const OrderSummary = t0 => {
  const $ = _c(69);
  const {
    cart,
    showCartItems: t1,
    showPromoCode: t2,
    onPromotionChange,
    displayCheckoutButton: t3,
    displayPaymentIcons: t4,
    allowQuantityEdits: t5,
    className
  } = t0;
  const showCartItems = t1 === undefined ? false : t1;
  const showPromoCode = t2 === undefined ? false : t2;
  const displayCheckoutButton = t3 === undefined ? false : t3;
  const displayPaymentIcons = t4 === undefined ? false : t4;
  const allowQuantityEdits = t5 === undefined ? false : t5;
  const [checkoutUrl, setCheckoutUrl] = useState("/checkout");
  let t6;
  let t7;
  if ($[0] !== displayCheckoutButton) {
    t6 = () => {
      if (displayCheckoutButton) {
        fetch("/api/checkout-url").then(_temp).then(data => {
          if (data.url) {
            setCheckoutUrl(data.url);
          }
        }).catch(_temp2);
      }
    };
    t7 = [displayCheckoutButton];
    $[0] = displayCheckoutButton;
    $[1] = t6;
    $[2] = t7;
  } else {
    t6 = $[1];
    t7 = $[2];
  }
  useEffect(t6, t7);
  if (!cart) {
    const t8 = `card-bg-gray sticky ${className || ""}`;
    let t9;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className="flex items-center gap-2 mb-6"><FontAwesomeIcon icon={faHiveIconSmallLight} className="text-primary text-lg" /><span className="text-xl text-ink-light-gray-bg">Order Summary</span></div>;
      $[3] = t9;
    } else {
      t9 = $[3];
    }
    let t10;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <div className="animate-pulse space-y-3"><div className="h-4 bg-gray-200 rounded w-3/4" /><div className="h-4 bg-gray-200 rounded w-1/2" /><div className="h-4 bg-gray-200 rounded w-2/3" /></div>;
      $[4] = t10;
    } else {
      t10 = $[4];
    }
    let t11;
    if ($[5] !== t8) {
      t11 = <div className={t8}>{t9}{t10}</div>;
      $[5] = t8;
      $[6] = t11;
    } else {
      t11 = $[6];
    }
    return t11;
  }
  const getImageForItem = _temp3;
  const t8 = `card-bg-gray sticky ${className || ""}`;
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex items-center gap-2 mb-6"><FontAwesomeIcon icon={faHiveIconSmallLight} className="text-primary text-lg" /><span className="text-xl text-ink-light-gray-bg">Order Summary</span></div>;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== allowQuantityEdits || $[9] !== cart.cartItems || $[10] !== cart.isTradeCustomer || $[11] !== showCartItems) {
    t10 = showCartItems && cart.cartItems?.length > 0 && <div className="mb-5">{cart.cartItems.map((cartItem, itemIndex) => <div key={`${cartItem.product.sub_product_id}-${itemIndex}`}>{getImageForItem(cartItem) && <div className="flex items-start border-0 border-b-1 pb-3 mb-3 gap-4"><Link href={`${cartItem.product?.url}`} className="relative w-full max-w-[70px] aspect-square text-center overlay-block mx-auto bg-white"><Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${getImageForItem(cartItem)}`} alt={decodeEntities(cartItem.product?.sub_product_name)} className="mx-auto max-w-[70px] h-auto" style={{
              objectFit: "contain"
            }} fill={true} quality={90} sizes="70px" /></Link><div className="flex-1 flex gap-3"><div className="flex-1 min-w-0 break-words"><div className="text-xs font-medium capitalize text-ink-dark-gray-bg mb-2"><Link href={cartItem.product.url}>{decodeEntities(cartItem.product.sub_product_name)}</Link></div>{cartItem.product?.custom_backorder_message ? <div className="text-xs mb-2"><span className="text-red">{cartItem.product.backorder_hold_message}</span> {cartItem.product.backorder_message}</div> : cartItem.product?.backordered && !cartItem.product?.in_inventory && cartItem.product?.backorder_message && <div className="text-xs mb-2"><span className="text-red">backordered</span> {cartItem.product.backorder_message}</div>}<div className="text-ink-light-gray-bg"><div className="text-sm mb-2"><span className="text-ink-light mr-1">Base Price:</span><Price amount={cartItem.product?.retail_price} className="font-medium" /></div><CartOptions options={cartItem.product.options} /><CartSwatches swatches={cartItem.product.swatches} /><InlineQtyEditor cartItem={cartItem} itemIndex={itemIndex} allowEdit={allowQuantityEdits} /><ul className="hidden"><li className="text-3xs font-light"><span className="text-3xs font-medium text-light">shipping:</span> free</li><li className="text-3xs font-light"><span className="text-3xs font-medium text-light">tilt:</span> free</li><li className="text-3xs font-light"><span className="text-3xs font-medium text-light">base:</span> free</li><li className="text-3xs font-light"><span className="text-3xs font-medium text-light">seat:</span> free</li><li className="text-3xs font-light"><span className="text-3xs font-medium text-light">armpad:</span> free</li></ul></div></div><div className="shrink-0 text-right"><Price amount={cartItem.total_price} className="text-xs font-light text-ink-medium-gray-bg" />{cartItem.original_total > cartItem.total_price && <div className="text-xs"><Price amount={cartItem.original_total} className="line-through text-light" /></div>}{cartItem.product?.on_sale == 1 && cartItem.product?.discount_percentage > 0 && <div className="text-red text-xs">{cartItem.product.discount_percentage}% off</div>}{cart.isTradeCustomer && cartItem.trade_price !== undefined && <div className="text-xs text-light">Trade Price <Price amount={cartItem.trade_price} /></div>}</div></div></div>}</div>)}</div>;
    $[8] = allowQuantityEdits;
    $[9] = cart.cartItems;
    $[10] = cart.isTradeCustomer;
    $[11] = showCartItems;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="text-sm text-ink-light-gray-bg font-normal">Subtotal</span>;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const t12 = cart.isTradeCustomer ? cart.retailSubTotal : cart.subTotal;
  let t13;
  if ($[14] !== t12) {
    t13 = <div className="flex items-start justify-between border-b-1 border-gray-10 pb-0.5 mb-6">{t11}<Price amount={t12} className="text-sm font-medium text-ink-medium-gray-bg" /></div>;
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== cart.isTradeCustomer || $[17] !== cart.tradeDiscount) {
    t14 = cart.isTradeCustomer && cart.tradeDiscount > 0 && <div className="flex items-start justify-between mb-6"><span className="text-sm text-ink-light-gray-bg font-normal">Trade Discount</span><div className="text-sm font-medium text-ink-medium-gray-bg">-<Price amount={cart.tradeDiscount} /></div></div>;
    $[16] = cart.isTradeCustomer;
    $[17] = cart.tradeDiscount;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== cart.isTradeCustomer || $[20] !== cart.totalSavings) {
    t15 = cart.totalSavings > 0 && !cart.isTradeCustomer && <div className="flex items-start justify-between mb-6"><span className="text-sm text-red font-normal">Total Savings</span><div className="text-sm font-medium text-red">-<Price amount={cart.totalSavings} /></div></div>;
    $[19] = cart.isTradeCustomer;
    $[20] = cart.totalSavings;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== cart.discount || $[23] !== cart.promotionDiscount) {
    t16 = (cart.promotionDiscount ?? cart.discount) > 0 && <div className="flex items-start justify-between mb-6"><span className="text-sm text-green-600 font-normal">Promotion Discount</span><div className="text-sm font-medium text-green-600">-<Price amount={cart.promotionDiscount ?? cart.discount} /></div></div>;
    $[22] = cart.discount;
    $[23] = cart.promotionDiscount;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== cart.tierDiscountInfo) {
    t17 = Array.isArray(cart.tierDiscountInfo) && cart.tierDiscountInfo.filter(_temp4).map(_temp6);
    $[25] = cart.tierDiscountInfo;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span className="text-sm text-ink-light-gray-bg font-normal">Shipping</span>;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== cart.shippingCost) {
    t19 = <div className="flex items-start justify-between border-b-1 border-gray-10 pb-0.5 mb-6">{t18}<div className="text-sm font-medium text-ink-medium-gray-bg"><Price amount={cart.shippingCost} /></div></div>;
    $[28] = cart.shippingCost;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <span className="text-sm text-ink-light-gray-bg font-normal">Tax</span>;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== cart.tax_to_be_determined) {
    t21 = cart.tax_to_be_determined && <span>TBD</span>;
    $[31] = cart.tax_to_be_determined;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== cart.tax || $[34] !== cart.tax_to_be_determined) {
    t22 = !cart.tax_to_be_determined && <Price amount={cart.tax} />;
    $[33] = cart.tax;
    $[34] = cart.tax_to_be_determined;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== t21 || $[37] !== t22) {
    t23 = <div className="flex items-start justify-between border-b-1 border-gray-10 pb-0.5 mb-6">{t20}<div className="text-sm font-medium text-ink-medium-gray-bg">{t21}{t22}</div></div>;
    $[36] = t21;
    $[37] = t22;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] !== onPromotionChange || $[40] !== showPromoCode) {
    t24 = showPromoCode && <div className="mb-6"><PromoCode onPromotionChange={onPromotionChange} /></div>;
    $[39] = onPromotionChange;
    $[40] = showPromoCode;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  const t25 = `text-sm text-ink-light-gray-bg font-normal ${cart.hasSaleItems ? "text-red" : ""}`;
  const t26 = cart.hasSaleItems ? "Sale Price" : "Total";
  let t27;
  if ($[42] !== t25 || $[43] !== t26) {
    t27 = <span className={t25}>{t26}</span>;
    $[42] = t25;
    $[43] = t26;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] !== cart.orderTotal) {
    t28 = <Price amount={cart.orderTotal} className="text-2xl font-medium text-ink-dark-gray-bg leading-none" />;
    $[45] = cart.orderTotal;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  let t29;
  if ($[47] !== t27 || $[48] !== t28) {
    t29 = <div className="mb-8 flex items-end justify-between">{t27}{t28}</div>;
    $[47] = t27;
    $[48] = t28;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== checkoutUrl || $[51] !== displayCheckoutButton) {
    t30 = displayCheckoutButton && <div className="mb-3"><Link href={checkoutUrl} onClick={() => console.log("Checkout clicked, URL:", checkoutUrl)} className="relative block btn btn-primary w-full"><FontAwesomeIcon icon={faLockKeyhole} className="absolute top-1/3 left-2 text-xs" />Checkout</Link></div>;
    $[50] = checkoutUrl;
    $[51] = displayCheckoutButton;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== displayPaymentIcons) {
    t31 = false && displayPaymentIcons && <div className="grid grid-cols-6 gap-2 mb-3"><Image src="/images/payment-logo-color-visa.png" alt="Visa" width={38} height={24} /><Image src="/images/payment-logo-color-mastercard.png" alt="Mastercard" width={38} height={24} /><Image src="/images/payment-logo-color-american-express.png" alt="American Express" width={38} height={24} /><Image src="/images/payment-logo-color-discover.png" alt="Discover" width={38} height={24} /><Image src="/images/payment-logo-color-apple.png" alt="Apple Pay" width={38} height={24} /><Image src="/images/payment-logo-color-google.png" alt="Google Pay" width={38} height={24} /><Image src="/images/payment-logo-color-paypal.png" alt="PayPal" width={38} height={24} /></div>;
    $[53] = displayPaymentIcons;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] !== t10 || $[56] !== t13 || $[57] !== t14 || $[58] !== t15 || $[59] !== t16 || $[60] !== t17 || $[61] !== t19 || $[62] !== t23 || $[63] !== t24 || $[64] !== t29 || $[65] !== t30 || $[66] !== t31 || $[67] !== t8) {
    t32 = <div className={t8}>{t9}{t10}{t13}{t14}{t15}{t16}{t17}{t19}{t23}{t24}{t29}{t30}{t31}</div>;
    $[55] = t10;
    $[56] = t13;
    $[57] = t14;
    $[58] = t15;
    $[59] = t16;
    $[60] = t17;
    $[61] = t19;
    $[62] = t23;
    $[63] = t24;
    $[64] = t29;
    $[65] = t30;
    $[66] = t31;
    $[67] = t8;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  return t32;
};
export default OrderSummary;
function _temp(response) {
  return response.json();
}
function _temp2(err) {
  console.error("Checkout URL fetch failed:", err);
}
function _temp3(savedItem) {
  if (savedItem.product?.variant?.file_name) {
    return savedItem.product.variant.file_name;
  }
  if (savedItem.product?.option_product_photo) {
    return `option_photos/${savedItem.product.option_product_photo}`;
  }
  if (savedItem.product?.thumb_1) {
    return `thumbs/${savedItem.product.thumb_1}`;
  }
  return savedItem.product?.product_photo || "";
}
function _temp4(g) {
  return g.brand_label && g.qualifying_spend > 0 && g.next_tier;
}
function _temp5(tier) {
  return <div key={tier.threshold} className="text-red leading-tight">Spend <Price amount={tier.threshold} /> Save {Math.round(tier.rate * 100)}%</div>;
}
function _temp6(g_0, i) {
  return <div key={i} className="mb-6 text-xs"><div className="text-light">Spend <Price amount={g_0.amount_to_next_tier} /> more and save {Math.round(g_0.next_tier.rate * 100)}% on {g_0.brand_label}</div>{g_0.tiers?.map(_temp5)}</div>;
}